import { useRef } from 'react';
import { Box, TextField, Typography } from '@mui/material';

interface OTPInputProps {
  value: string;
  onChange: (val: string) => void;
  length: number;
  hasError?: boolean;
  errorMessage?: string;
}

export const OTPInput = ({
  value,
  onChange,
  length,
  hasError = false,
  errorMessage,
}: OTPInputProps) => {
  const refList = useRef<Array<HTMLDivElement | null>>([]);
  const valueList = value.padEnd(6, ' ').split('');

  const handleInputChange = (val: string, index: number) => {
    const newValueList = [...valueList];
    newValueList[index] = val || ' ';
    const newValue = newValueList.join('');

    onChange(newValue);

    if (refList.current && index < length - 1 && val) {
      refList.current[index + 1]?.focus();
    }
    if (refList.current && index > 0 && !val) {
      refList.current[index - 1]?.focus();
    }
  };

  const handleKeyDown = (key: string, index: number) => {
    const regex = new RegExp('^(?:[0-9]{1})?$');

    if (regex.test(key)) {
      handleInputChange(key, index);
    } else {
      switch (key) {
        case 'Backspace':
          handleInputChange('', index);
          break;
        case 'ArrowLeft':
          if (index > 0) {
            refList.current[index - 1]?.focus();
          }
          break;
        case 'ArrowRight':
          if (index < length - 1) {
            refList.current[index + 1]?.focus();
          }
          break;
        default:
          break;
      }
    }
  };

  const handleFocus = (event: { target: { select: () => void } }) => {
    event.target.select();
  };

  const handlePaste = async () => {
    const content = await navigator.clipboard.readText();
    const paddedContent = content.padEnd(6, ' ');
    onChange(paddedContent);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          gap: '10px',
        }}
      >
        {Array(length)
          .fill('')
          .map((_, index) => (
            <TextField
              key={index}
              inputRef={el => (refList.current[index] = el)}
              value={valueList[index] || ''}
              error={hasError}
              slotProps={{ htmlInput: { maxLength: 1 } }}
              sx={{
                input: { textAlign: 'center' },
                minWidth: '45px',
                maxWidth: '60px',
              }}
              onKeyDown={e => handleKeyDown(e.key, index)}
              onFocus={handleFocus}
              onPaste={handlePaste}
              autoFocus={index === 0}
            />
          ))}
      </Box>
      {hasError && errorMessage && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: '400',
            width: '100%',
            color: theme => theme.palette.red.main,
          }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};
